import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcSweepsV1SweepPlanQueryServiceSweepPlanGet } from '@interxfi/api-client/generated/hooks/ixc-sweeps-v1-sweep-plan-query-service-sweep-plan-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcSweepsV1SweepPlanState } from '@interxfi/types';
import { SweepPlanComponentsTable } from '../../../../../../features/sweeps/plan/sweep-plan-components-table';
import { SweepPlanEntityMenu } from '../../../../../../features/sweeps/plan/sweep-plan-entity-menu';
import { SweepPlanEventSummary } from '../../../../../../features/sweeps/plan/sweep-plan-event';
import { getSweepPlanStateListItems } from '../../../../../../features/sweeps/plan/sweep-plan-list-items';
import { getSweepPlanDetailPath } from '../../../../../../features/sweeps/plan/sweep-plan-routing';
import { SweepPlanStatusPill } from '../../../../../../features/sweeps/plan/sweep-plan-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/sweep/plan/_plan/$sweepPlanId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `sweep-plan-${params.sweepPlanId}`,
        label: 'Plan',
        to: getSweepPlanDetailPath(params.sweepPlanId),
        suffix: (
          <CopyButton
            content={params.sweepPlanId}
            label="Copy Sweep Plan ID"
            tooltip={{ copy: params.sweepPlanId, copied: 'Copied Sweep Plan ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Sweep Plan'),
  component: SweepPlanDetail,
});

function getListItems(data: IxcSweepsV1SweepPlanState | undefined) {
  const listItems = getSweepPlanStateListItems(data);

  return {
    general: {
      left: [listItems.data.name] satisfies ReadonlyListItem[],
      right: [listItems.sweepPlanId] satisfies ReadonlyListItem[],
    },
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load sweep plan', 'view this sweep plan');

function SweepPlanDetail() {
  const { sweepPlanId } = Route.useParams();
  const request = { sweepPlanId } as const;
  const { data, error, isPending } = useIxcSweepsV1SweepPlanQueryServiceSweepPlanGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.sweepPlan), [data?.sweepPlan]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <SweepPlanStatusPill isPending={showPending} size="large" status={data?.sweepPlan?.status} />
            </HorizontalStack>

            <SweepPlanEntityMenu {...request} />
          </PageHeader>

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <SweepPlanEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Components</Typography.Level3>

          <SweepPlanComponentsTable data={data?.sweepPlan?.data?.components || []} showSkeleton={showPending} />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
